import type { ComponentProps } from 'react';
import type React from 'react';
import { useEffect } from 'react';

import { parseHtml } from '@change-corgi/core-react/html';
import { Box } from '@change-corgi/design-system-legacy/layout';

type Props = {
	html: string;
	aspectRatio?: number;
};

const EmbeddedMediaInner: React.FC<Props & ComponentProps<typeof Box>> = ({ html, aspectRatio, ...rest }) => {
	return (
		<Box
			sx={{
				'&>iframe': {
					height: aspectRatio ? 'auto' : undefined,
					aspectRatio,
				},
			}}
			suppressHydrationWarning
			{...rest}
		>
			{parseHtml(html, {
				allowedTags: ['iframe'],
				allowedAttrs: [
					'scrolling',
					'frameborder',
					'allowfullscreen',
					'allow',
					// https://iframely.com/docs/lazy-load#what-you-get
					'data-iframely-url',
					'data-img',
				],
				suppressHydrationWarning: true,
			})}
		</Box>
	);
};

const SCRIPT_ID = 'iframely-embed-script';

export const EmbeddedMedia: React.FC<Props & ComponentProps<typeof Box>> = (props) => {
	const { html } = props;
	const accessibleHtml = html.replace(/<iframe\b/gi, '<iframe aria-label="Media"');

	useEffect(() => {
		if (document.getElementById(SCRIPT_ID)) return;
		const script = document.createElement('script');
		script.id = SCRIPT_ID;
		script.async = true;
		script.setAttribute('charset', 'utf-8');
		script.src = '//cdn.iframe.ly/embed.js';
		document.head.appendChild(script);
	}, []);

	return <EmbeddedMediaInner {...props} html={accessibleHtml} />;
};
